import { useMutation, useQuery } from '@apollo/client/react';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { AppbarBackButton, AppbarTitle, ScreenAppbar } from '@/components/atoms/appbar';
import { OutlineButton, PrimaryButton } from '@/components/atoms/button';
import { ConfirmDialog } from '@/components/atoms/dialog';
import { ActionFooterBar } from '@/components/atoms/footer';
import { ErrorState, LoadingState } from '@/components/atoms/state';
import {
  GetContactDocument,
  GetHandoffDocument,
  SendSmsHandoffDocument,
} from '@/generated/usermobile-bff/graphql';
import { useTranslation } from '@/i18n';
import { parseContactIdParam } from '@/models/handoffRouteParams';
import { useGlobalAlertStore } from '@/stores/useGlobalAlertStore';
import { usePendingSmsStore } from '@/stores/usePendingSmsStore';
import { radii, spacing, unisonColors, useAppTheme } from '@/theme';

const MAX_BODY_LENGTH = 600;
const BODY_INPUT_MIN_HEIGHT = 240;

interface HandoffSmsComposeProps {
  readonly id: string;
}

export const HandoffSmsCompose = ({ id }: HandoffSmsComposeProps) => {
  const { t } = useTranslation();
  const { colors } = useAppTheme();
  const router = useRouter();
  const showGlobalAlert = useGlobalAlertStore((state) => state.showGlobalAlert);
  const addPendingSms = usePendingSmsStore((state) => state.addPendingSms);

  const [body, setBody] = useState('');
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showBodyRequiredError, setShowBodyRequiredError] = useState(false);

  const { data, loading, error } = useQuery(GetHandoffDocument, {
    variables: { id },
    fetchPolicy: 'cache-first',
  });

  const contactId = data?.handoff.contactId ?? null;
  const numericContactId = parseContactIdParam(contactId);

  const { data: contactData } = useQuery(GetContactDocument, {
    variables: { id: numericContactId ?? 0 },
    fetchPolicy: 'cache-first',
    skip: numericContactId == null,
  });

  const [sendSmsHandoff] = useMutation(SendSmsHandoffDocument, {
    refetchQueries: [{ query: GetHandoffDocument, variables: { id } }],
  });

  const handleSendPress = useCallback(() => {
    if (body.trim() === '') {
      setShowBodyRequiredError(true);

      return;
    }

    setConfirmVisible(true);
  }, [body]);

  const handleBodyChange = useCallback((text: string) => {
    setShowBodyRequiredError(false);
    setBody(text);
  }, []);

  const handleConfirmSend = useCallback(async () => {
    setConfirmVisible(false);
    setIsSending(true);

    try {
      const result = await sendSmsHandoff({
        variables: { input: { handoffId: id, messageBody: body } },
      });

      const messageId = result.data?.sendSmsHandoff.messageId;

      // The chat row is created by a worker, so the chat shows this message from memory
      // until the row carrying the same messageId arrives.
      if (messageId != null && contactId != null) {
        addPendingSms({ contactId, messageId, body, sentAt: new Date().toISOString() });
      }

      showGlobalAlert({ text: t('aiTasks.detail.smsCompose.sent'), variant: 'success' });
      router.back();
    } catch {
      showGlobalAlert({ text: t('aiTasks.detail.smsCompose.sendFailed'), variant: 'error' });
    } finally {
      setIsSending(false);
    }
  }, [addPendingSms, body, contactId, id, router, sendSmsHandoff, showGlobalAlert, t]);

  const isOverLimit = body.length > MAX_BODY_LENGTH;

  if (loading && !data) {
    return <LoadingState />;
  }

  if (error && !data) {
    return <ErrorState message={t('aiTasks.detail.loadError')} />;
  }

  return (
    <View style={styles.container}>
      <ScreenAppbar testID="handoff-sms-appbar" variant="dark">
        <AppbarBackButton
          testID="handoff-sms-back-button"
          variant="dark"
          onPress={() => router.back()}
        />
        <AppbarTitle
          testID="handoff-sms-title"
          title={t('aiTasks.detail.smsCompose.title')}
          variant="dark"
        />
      </ScreenAppbar>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={styles.keyboardArea}>
        <ScrollView contentContainerStyle={styles.body}>
          <View style={styles.card}>
            <Text style={[styles.fieldLabel, { color: colors.textNdSecondary }]}>
              {t('aiTasks.detail.smsCompose.lastNameLabel')}
            </Text>
            <Text style={[styles.fieldValue, { color: colors.textNdPrimary }]}>
              {contactData?.contact.lastName ?? '-'}
            </Text>
            <Text style={[styles.fieldLabel, styles.bodyLabel, { color: colors.textNdSecondary }]}>
              {t('aiTasks.detail.smsCompose.bodyLabel')}
            </Text>
            <TextInput
              multiline
              placeholder={t('aiTasks.detail.smsCompose.bodyPlaceholder')}
              placeholderTextColor={colors.textNdMuted}
              style={[styles.bodyInput, { color: colors.textNdPrimary }]}
              testID="handoff-sms-body-input"
              textAlignVertical="top"
              value={body}
              onChangeText={handleBodyChange}
            />
            <Text
              style={[
                styles.charCount,
                isOverLimit ? styles.charCountError : { color: colors.textNdPrimary },
              ]}
              testID="handoff-sms-char-count">
              {t('aiTasks.detail.smsCompose.charCount', { length: body.length })}
            </Text>
            {isOverLimit ? (
              <Text style={styles.errorText} testID="handoff-sms-limit-error">
                {t('aiTasks.detail.smsCompose.charLimit')}
              </Text>
            ) : null}
            {showBodyRequiredError ? (
              <Text style={styles.errorText} testID="handoff-sms-required-error">
                {t('aiTasks.detail.smsCompose.bodyRequired')}
              </Text>
            ) : null}
          </View>
        </ScrollView>
        <ActionFooterBar testID="handoff-sms-footer">
          <OutlineButton testID="handoff-sms-cancel" onPress={() => router.back()}>
            {t('aiTasks.detail.smsCompose.cancel')}
          </OutlineButton>
          <PrimaryButton
            disabled={isSending || isOverLimit}
            loading={isSending}
            testID="handoff-sms-send"
            onPress={handleSendPress}>
            {t('aiTasks.detail.smsCompose.send')}
          </PrimaryButton>
        </ActionFooterBar>
      </KeyboardAvoidingView>
      <ConfirmDialog
        body={t('aiTasks.detail.smsCompose.confirmBody')}
        confirmLabel={t('aiTasks.detail.smsCompose.confirm')}
        dismissLabel={t('aiTasks.detail.smsCompose.dismiss')}
        testID="handoff-sms-confirm-dialog"
        title={t('aiTasks.detail.smsCompose.confirmTitle')}
        visible={confirmVisible}
        onConfirm={() => void handleConfirmSend()}
        onDismiss={() => setConfirmVisible(false)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: unisonColors.bgNlWeak,
    flex: 1,
  },
  keyboardArea: {
    flex: 1,
  },
  body: {
    padding: spacing[4],
  },
  card: {
    backgroundColor: unisonColors.white,
    borderRadius: radii.md,
    padding: spacing[4],
    rowGap: spacing[1],
  },
  fieldLabel: {
    fontSize: 12,
    lineHeight: 16,
  },
  fieldValue: {
    borderBottomColor: unisonColors.borderNdBase,
    borderBottomWidth: StyleSheet.hairlineWidth,
    fontSize: 16,
    letterSpacing: 0.5,
    lineHeight: 24,
    paddingBottom: spacing[2],
  },
  bodyLabel: {
    paddingTop: spacing[3],
  },
  bodyInput: {
    fontSize: 16,
    lineHeight: 24,
    minHeight: BODY_INPUT_MIN_HEIGHT,
    padding: 0,
  },
  charCount: {
    borderTopColor: unisonColors.borderNdBase,
    borderTopWidth: StyleSheet.hairlineWidth,
    fontSize: 14,
    lineHeight: 20,
    paddingTop: spacing[2],
  },
  charCountError: {
    color: unisonColors.error,
    fontWeight: '700',
  },
  errorText: {
    color: unisonColors.error,
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    paddingTop: spacing[1],
  },
});
